import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  ChartDataFilter,
  DateRanges,
} from "../../stories/components/PerformanceChart/types";
import {
  convertFavoritedAccountEntity,
  FavoritedAccount,
  FavoritedAccountEntity,
} from "../models/favoritedAccount";
import { Studio } from "../models/studio";
import {
  FETCH_STATES,
  makeBackendGetCallWithJsonResponse,
  makeBackendPostCallWithJsonResponse,
} from "../utils/fetch";
import {
  FAVORITED_ACCOUNT_API,
  FETCH_ALL_USER_FAVORITES,
  FETCH_PENDING_PROJECT_COUNTS,
} from "../utils/routes";
import { receiveErrors } from "./errorStore";
import { fetchFavorites } from "./favorites";

interface PendingProjectCounts {
  pendingScheduledProjectCount: number;
  pendingRecordingSessionBookingCount: number;
  fetchStatus: FETCH_STATES;
}
interface UserFavorites {
  liked_users: number[];
  liked_studios: number[];
  liked_studio_rooms: number[];
}

interface UserDashboard {
  favoritedAccounts: FavoritedAccount[];
  pendingProjects: PendingProjectCounts;
  userFavorites: UserFavorites | null;
}

const initialState: UserDashboard = {
  favoritedAccounts: [],
  pendingProjects: {
    pendingScheduledProjectCount: 0,
    pendingRecordingSessionBookingCount: 0,
    fetchStatus: FETCH_STATES.IDLE,
  },
  userFavorites: null,
};

export enum AccountType {
  ARTIST,
  ENGINEER,
  STUDIO,
}

export interface fetchChartDataParams {
  studio_ids?: number[];
  data_filter: ChartDataFilter;
  start_date: string;
  end_date: string;
  date_range_filter: DateRanges;
  filter_by?: number[];
  userStudios?: Studio[];
  user_id?: number; // This is for superusers only
  selected_account_type?: AccountType;
}

export interface DataPoint {
  x: string;
  y: number;
}
export interface ChartData {
  id: string;
  grand_total: number;
  color?: string;
  data: DataPoint[];
}
export interface FetchChartDataResponse {
  chartData: ChartData[];
  currentDate: string;
  dateFormat: string;
  grandTotal: number;
  previousDateRangeData: number;
  previousStartDate: string;
  previousEndDate: string;
  grossVolumePercentage: number;
  sessionsBookedPercentage: number;
  projectsBookedPercentage: number;
  profileVisitsPercentage: number;
  productsSoldPercentage: number;
}

interface createFavoritedAccountParams {
  liked_user?: number;
  liked_studio?: number;
  liked_studio_room?: number;
}

export const createFavoritedAccount = createAsyncThunk(
  FAVORITED_ACCOUNT_API + "-post",
  async (favoritedAccount: createFavoritedAccountParams, thunkAPI) => {
    const result = await makeBackendPostCallWithJsonResponse<FavoritedAccount>(
      FAVORITED_ACCOUNT_API,
      favoritedAccount,
    );
    if (result.success) {
      return result.resultJson;
    }
    const errors = { errors: result.resultJson };
    thunkAPI.dispatch(receiveErrors(errors));
    return thunkAPI.rejectWithValue(errors);
  },
);

export const deleteFavoritedAccount = createAsyncThunk(
  FAVORITED_ACCOUNT_API + "-delete",
  async (favoritedAccountParams: createFavoritedAccountParams, thunkAPI) => {
    const result = await makeBackendPostCallWithJsonResponse<FavoritedAccount>(
      FAVORITED_ACCOUNT_API,
      { ...favoritedAccountParams, delete: true },
    );
    if (result.success) {
      return result.resultJson;
    }
    const errors = { errors: result.resultJson };
    thunkAPI.dispatch(receiveErrors(errors));
    return thunkAPI.rejectWithValue(errors);
  },
);

export const fetchAllUserFavorites = createAsyncThunk(
  FETCH_ALL_USER_FAVORITES,
  async (_, thunkAPI) => {
    const result = await makeBackendGetCallWithJsonResponse<UserFavorites>(
      FETCH_ALL_USER_FAVORITES,
      "",
    );
    if (result.success) {
      return result.resultJson;
    }
    const errors = { errors: result.resultJson };
    thunkAPI.dispatch(receiveErrors(errors));
    return thunkAPI.rejectWithValue(errors);
  },
);

export const fetchPendingProjectCounts = createAsyncThunk(
  FETCH_PENDING_PROJECT_COUNTS,
  async (_, thunkAPI) => {
    const result = await makeBackendGetCallWithJsonResponse<{
      pending_scheduled_projects_count: number;
      pending_recording_session_bookings_count: number;
    }>(FETCH_PENDING_PROJECT_COUNTS, "");
    if (result.success) {
      return result.resultJson;
    }
    const errors = { errors: result.resultJson };
    thunkAPI.dispatch(receiveErrors(errors));
    return thunkAPI.rejectWithValue(errors);
  },
);

export const userDashboardSlice = createSlice({
  name: "userDashboardSlice",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchPendingProjectCounts.pending, (state) => {
      state.pendingProjects = {
        ...state.pendingProjects,
        fetchStatus: FETCH_STATES.LOADING,
      };
    });
    builder.addCase(fetchPendingProjectCounts.fulfilled, (state, action) => {
      state.pendingProjects = {
        pendingScheduledProjectCount:
          action.payload.pending_scheduled_projects_count,
        pendingRecordingSessionBookingCount:
          action.payload.pending_recording_session_bookings_count,
        fetchStatus: FETCH_STATES.LOADED,
      };
    });
    builder.addCase(fetchPendingProjectCounts.rejected, (state) => {
      state.pendingProjects = {
        pendingScheduledProjectCount: 0,
        pendingRecordingSessionBookingCount: 0,
        fetchStatus: FETCH_STATES.FAILED,
      };
    });

    builder.addCase(createFavoritedAccount.fulfilled, (state, action) => {
      state.favoritedAccounts.push(action.payload);
      if (state.userFavorites) {
        const { liked_user, liked_studio, liked_studio_room } = action.payload;
        if (liked_user) {
          state.userFavorites.liked_users.push(liked_user);
        }
        if (liked_studio) {
          state.userFavorites.liked_studios.push(liked_studio);
        }
        if (liked_studio_room) {
          state.userFavorites.liked_studio_rooms.push(liked_studio_room);
        }
      }
    });
    builder.addCase(fetchFavorites.fulfilled, (state, action) => {
      const data: FavoritedAccountEntity[] = action.payload.data;
      data.forEach((account) => {
        const favoritedAccount: FavoritedAccount =
          convertFavoritedAccountEntity(account);
        if (
          !state.favoritedAccounts.some(
            (stateFavoritedAccount) =>
              stateFavoritedAccount.id === favoritedAccount.id,
          )
        ) {
          state.favoritedAccounts.push(favoritedAccount);
        }
      });
    });
    builder.addCase(deleteFavoritedAccount.fulfilled, (state, action) => {
      const index = state.favoritedAccounts.findIndex(
        (favoritedAccount) => favoritedAccount.id === action.payload.id,
      );
      state.favoritedAccounts.splice(index, 1);

      if (state.userFavorites) {
        const { liked_user, liked_studio, liked_studio_room } = action.payload;

        if (liked_user) {
          state.userFavorites.liked_users =
            state.userFavorites.liked_users.filter(
              (user) => user !== liked_user,
            );
        }
        if (liked_studio) {
          state.userFavorites.liked_studios =
            state.userFavorites.liked_studios.filter(
              (studio) => studio !== liked_studio,
            );
        }
        if (liked_studio_room) {
          state.userFavorites.liked_studio_rooms =
            state.userFavorites.liked_studio_rooms.filter(
              (studio_room) => studio_room !== liked_studio_room,
            );
        }
      }
    });
    builder.addCase(fetchAllUserFavorites.fulfilled, (state, action) => {
      state.userFavorites = action.payload;
    });
  },
});

export default userDashboardSlice.reducer;
